import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { formatCount } from "@/lib/format";
import { tp } from "@/lib/plural";

import { PreliminaryBadge } from "../adherence/PreliminaryBadge";
import { NO_VALUE } from "../sectionState";
import { adherenceUnavailableHint, investmentFigures, monthsText, type Figure } from "./summaryText";
import type { DiagnosticSummary } from "./summaryData";

/**
 * A faixa de destaque do Resumo (EF-DIAG-004 RF-02): a frase de abertura e os
 * quatro números. Os valores são os dos atos, e a fonte que falta aparece como
 * estado, e não como zero.
 *
 * A frase é montada de um modelo a partir da aderência e do prazo, sem edição.
 * Com só um dos dois, cita só ele; sem nenhum, não há frase.
 */
export function SummaryHero({
  summary,
  clientName,
}: {
  summary: DiagnosticSummary;
  clientName: string;
}) {
  const { t } = useTranslation();
  const { adherence } = summary.diagnosis;
  const figures = investmentFigures(summary.investment, t);

  const consolidated = adherence.kind === "counted" ? adherence.index.rows.consolidated : undefined;
  const standardPct = consolidated?.standard.pct ?? undefined;
  const index = adherence.kind === "counted" ? adherence.index : undefined;
  const preliminary = index?.preliminary === true;
  const months = monthsText(summary.investment.timeline);

  // Com índice preliminar a frase leva o mesmo estado do selo: o percentual ainda
  // pode mudar quando os itens sem disposição forem decididos.
  function headline(): string | undefined {
    if (standardPct !== undefined && months !== undefined) {
      const vars = { pct: standardPct, client: clientName, months };
      return preliminary
        ? t(
            "By the preliminary analysis, {{pct}}% of what {{client}} uses today runs on the SAP Cloud ERP Public standard, and the migration takes {{months}} months.",
            vars,
          )
        : t(
            "{{pct}}% of what {{client}} uses today runs on the SAP Cloud ERP Public standard, and the migration takes {{months}} months.",
            vars,
          );
    }
    if (standardPct !== undefined) {
      const vars = { pct: standardPct, client: clientName };
      return preliminary
        ? t(
            "By the preliminary analysis, {{pct}}% of what {{client}} uses today runs on the SAP Cloud ERP Public standard.",
            vars,
          )
        : t("{{pct}}% of what {{client}} uses today runs on the SAP Cloud ERP Public standard.", vars);
    }
    if (months !== undefined) {
      return t("The migration of {{client}} takes {{months}} months.", { client: clientName, months });
    }
    return undefined;
  }

  // Como o tile do Ato 1: cobertura e GAPs do Registro (não o número de vermelhos).
  function adherenceHint(): string | undefined {
    if (!index || standardPct === undefined) return adherenceUnavailableHint(adherence, t);
    const gaps = tp(
      t,
      index.gaps.total,
      "{{count}} GAP ({{essential}} essential)",
      "{{count}} GAPs ({{essential}} essential)",
      { essential: formatCount(index.gaps.essential) },
    );
    return consolidated?.coverage_pct != null
      ? `${t("Solution coverage {{pct}}%", { pct: consolidated.coverage_pct })} · ${gaps}`
      : gaps;
  }

  const text = headline();

  return (
    <section
      data-testid="summary-highlights"
      className="flex min-w-0 flex-col gap-4 rounded-lg border-2 border-dashed border-[#ffc83d] bg-white p-6"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#7b8799]">
        {t("In one page")}
      </div>

      {text && (
        <h2
          data-testid="summary-headline"
          className="m-0 max-w-[60ch] text-[22px] font-extrabold leading-snug text-[#08254c] sm:text-[26px]"
        >
          {text}
        </h2>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Highlight
          testId="summary-adherence"
          label={t("Standard fit")}
          labelExtra={preliminary && <PreliminaryBadge />}
          value={standardPct === undefined ? NO_VALUE : `${standardPct}%`}
          hint={adherenceHint()}
          note={
            index?.preliminary
              ? tp(
                  t,
                  index.undecided_count,
                  "{{count}} item with no disposition",
                  "{{count}} items with no disposition",
                )
              : undefined
          }
        />
        <Highlight testId="summary-software" {...figures.software} />
        <Highlight testId="summary-project" {...figures.project} />
        <Highlight testId="summary-timeline" {...figures.timeline} />
      </div>
    </section>
  );
}

function Highlight({
  testId,
  label,
  labelExtra,
  value,
  hint,
  note,
}: Figure & { testId: string; labelExtra?: ReactNode; note?: string }) {
  return (
    <div data-testid={testId} className="flex min-w-0 flex-col gap-0.5 border-l-[3px] border-[#ffc83d] pl-3">
      <div className="flex flex-wrap items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
        {label}
        {labelExtra}
      </div>
      <b className="break-words text-[26px] font-extrabold leading-tight tabular-nums text-[#08254c] sm:text-[28px]">
        {value}
      </b>
      {hint && <small className="text-[12px] leading-snug text-[#5a6b84]">{hint}</small>}
      {note && <small className="text-[11px] leading-snug text-[#c77700]">{note}</small>}
    </div>
  );
}
